/****************
 * WHICH VENUES A VIEWER MAY SEE.
 *
 * Guillaume, 2026-09-09: "I would like the MEXC Traders to be there too but
 * ONLY FOR ADMIN, I will test them and when the test is over we need to make it
 * available for everybody."
 *
 * The second half is why this file exists. "Available for everybody" has to be
 * ONE edit — flip MEXC_IS_PUBLIC below — not a hunt through four screens for
 * whichever ones remembered to ask. Every roster surface asks this function; a
 * new one that forgets is the only way MEXC can be wrong again.
 *
 * ── What was actually happening ─────────────────────────────────────────────
 *
 * Three of the four surfaces had it backwards, and none of it was visible
 * because only one file mentioned venues at all:
 *
 *   Traders roster    isAdmin ? venueFilter : 'all'  — non-admins got EVERYTHING
 *   Dashboard roster  no venue filter whatsoever      — everyone got everything
 *   Copy Trading      hardcoded 'binance'             — admins got nothing
 *   Traders positions isAdmin ? venueFilter : 'binance' — the only correct one
 *
 * So MEXC was already public in two places and hidden from the one person who
 * wanted it. The fix is not four fixes; it is one answer four screens ask.
 ****************/

/**
 * Flip to `true` when MEXC testing is done and its traders go public.
 *
 * That is the WHOLE change. Nothing else needs editing: every surface derives
 * its venue from `venueScope`, and the admin chooser keeps working either way —
 * it just stops being the only door.
 */
export const MEXC_IS_PUBLIC = true;

/**
 * What the API's `?venue=` accepts. 'all' means every PUBLIC venue, not a venue.
 *
 * 'hyperliquid' is admin-only for now (Guillaume, 2026-10-01), and there is no
 * flag for it in this file on purpose. The server decides what 'all' contains
 * (backend/internal/venuepolicy), so a customer, who is always on 'all', never
 * receives a Hyperliquid trader; an admin reaches them by picking the venue in
 * the chooser. Going public is one constant on the server, not three files.
 */
export type VenueScope = 'binance' | 'mexc' | 'hyperliquid' | 'all';

/**
 * The venues this viewer may see.
 *
 * `chosen` is the admin's own filter where a screen offers one — the Traders
 * page has a binance/mexc/all chooser. It is honoured only for someone who is
 * allowed the wider set; for everyone else it is ignored rather than trusted,
 * because a value that reaches this function from component state is not a
 * permission.
 */
export function venueScope(isAdmin: boolean, chosen?: VenueScope): VenueScope {
  // An admin's chooser is the only thing that narrows anything.
  if (isAdmin) return chosen ?? 'all';

  /*
   * Everyone else sees every venue, always, and `chosen` is ignored outright rather than
   * defaulted around. Guillaume, 2026-09-12: "the user should always have the 'all' vision
   * only." They have no chooser, so a value reaching here for a customer is stale state or
   * a hand-edited URL, and neither is an instruction.
   */
  if (MEXC_IS_PUBLIC) return 'all';

  /*
   * Everyone else, unconditionally.
   *
   * Not `chosen ?? 'binance'`: a non-admin must not be able to widen their own
   * scope by any route, including a chooser that should not have been rendered
   * for them. The server defaults to binance too — this is the client half of
   * the same rule, not the only half.
   */
  return 'binance';
}

/**
 * Whether to offer the venue chooser at all.
 *
 * While MEXC is admin-only the control is meaningless to everyone else — it
 * would present a choice whose options are all the same list. Once MEXC is
 * public it becomes a genuine filter and everyone gets it.
 */
export function canChooseVenue(isAdmin: boolean): boolean {
  /*
   * Admin only, and it stays that way now MEXC is public.
   *
   * Guillaume, 2026-09-12: "these buttons are only for admin, the user should always have
   * the 'all' vision only." Seeing every venue and choosing between them are two different
   * permissions, and this used to conflate them -- `MEXC_IS_PUBLIC || isAdmin` handed the
   * chooser to everyone the moment the flag flipped. A customer has no use for it: their
   * scope is 'all' whatever they press, so the control could only ever narrow what they
   * see for no reason they were given.
   */
  return isAdmin;
}

/**
 * Does this trader's venue price its own positions?
 *
 * Guillaume, 2026-10-01: every displayed price comes from the venue the row
 * belongs to. The live price stream is Binance's. For a Hyperliquid trader it
 * is the wrong market: the open-position mark and the unrealised PnL must be
 * Hyperliquid's own, which the server sends on the row (refreshed about once a
 * minute), and a Binance tick must not replace them. On 2026-10-02 it did, and
 * the profile's Unrealised PnL disagreed with its own open-trades table.
 */
export function usesOwnVenueMark(traderId?: string | null): boolean {
  return String(traderId ?? '').startsWith('hl:');
}
